import _extends from "@babel/runtime/helpers/extends";
import _inheritsLoose from "@babel/runtime/helpers/inheritsLoose";
import React, { Component } from "react";
import { formInfoSelector, removeForm } from "@icg360/rex";
import { Provider } from "react-redux";
import { Alert, Button } from "@icg360/ui-toolkit";
import store from "../store";
import { APP_IDS, PRELOAD, LOADING, LOADED, LOAD_ERROR } from "../constants";
import getPacData from "../promises/getPacData";
import { pacPropTypes, pacPropTypeDefaults } from "../shared/propTypes";
import { extractDataFromPolicyXml } from "../shared/xmlUtils";
import Form from "./Form";
import Spinner from "../shared/components/Spinner";
import isEqual from "lodash.isequal";
/**
 * This is our root component. Based on the props it receives when invoked,
 * it talks to the server to get the dynamic data needed to render a form.
 * If something goes wrong, you get a message indicating so.
 * If everything goes as according to plan, we setup the primary data structure
 * for the form (`pacData`) and mount it's component (`<Form>`).
 *
 * Note: if any of the incoming props for the component change, we will consider
 * any state the app is in as invalid and we'll mimic the functionality of
 * unmounting the component, cleaning up after ourselves, and then remounting it
 * with the new incoming props.
 */

var Pac = /*#__PURE__*/function (_Component) {
  _inheritsLoose(Pac, _Component);

  function Pac(props) {
    var _this;

    _this = _Component.call(this, props) || this;

    _this.initializePac = function () {
      var pacData = _this.state.pacData;

      _this.setState({
        loadState: LOADING
      });

      getPacData(pacData).then(function (_ref) {
        var rexFormMetadata = _ref.rexFormMetadata,
            supplementalPolicyTerms = _ref.supplementalPolicyTerms,
            productFormExists = _ref.productFormExists;

        _this.setState({
          loadState: LOADED,
          pacData: _extends({}, pacData, {
            rexFormMetadata: rexFormMetadata,
            supplementalPolicyTerms: supplementalPolicyTerms
          }),
          productFormExists: productFormExists
        });
      }).catch(function (err) {
        _this.setState({
          loadState: LOAD_ERROR,
          loadError: err.message
        });
      });
    };

    _this.cleanupAfterPac = function () {
      var pacId = _this.state.pacData.pacId;

      if (formInfoSelector(store.getState()).formIds.indexOf(pacId) !== -1) {
        store.dispatch(removeForm(pacId));
      }
    };

    _this.state = getInitialStateFromProps(props);
    return _this;
  }

  var _proto = Pac.prototype;

  _proto.componentDidMount = function componentDidMount() {
    this.initializePac();
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    this.cleanupAfterPac();
  };

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    if (!isEqual(prevProps, this.props)) {
      this.cleanupAfterPac();
      this.setState(getInitialStateFromProps(this.props), this.initializePac);
    }
  }
  /**
   * Initialize Pac
   * Fetch the product form. If everything goes ok, the form gets rendered;
   * otherwise you see an error and we never even bother trying to render
   * the <Form />.
   *
   * It's worth noting that the entire app depends on whether or not the product
   * form (with the dynamic inputs) exists and loads.
   */
  ;

  _proto.render = function render() {
    var _this$state = this.state,
        loadState = _this$state.loadState,
        loadError = _this$state.loadError,
        productFormExists = _this$state.productFormExists,
        pacData = _this$state.pacData,
        _this$state$pacData = _this$state.pacData,
        applicationId = _this$state$pacData.applicationId,
        handleExternalClose = _this$state$pacData.handleExternalClose,
        policyActionId = _this$state$pacData.policyActionId;
    return /*#__PURE__*/React.createElement("div", null, (loadState === LOADING || loadState === PRELOAD) && /*#__PURE__*/React.createElement(Spinner, {
      text: "Loading form..."
    }), loadState === LOAD_ERROR && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Alert, {
      bsStyle: "danger"
    }, /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("strong", null, "Error loading form."), " ", loadError), /*#__PURE__*/React.createElement("p", null, "If this error continues to persist, please contact", applicationId === APP_IDS.AGENT_PORTAL ? " policy services at 801-921-0748." : " technical support.")), /*#__PURE__*/React.createElement(Button, {
      onClick: handleExternalClose
    }, "Cancel")),
    /* The product data we fetch from the server has many forms in it. It's
       possible we fetch the product forms but the particular form we're
       trying to render doesn't exist in that set, in which case we'll
       show a message indicating as much. */
    loadState === LOADED && (productFormExists ? /*#__PURE__*/React.createElement(Provider, {
      store: store
    }, /*#__PURE__*/React.createElement(Form, {
      pacData: pacData,
      columnData: this.props.columnData,
      columnDataTitle: this.props.columnDataTitle
    })) : /*#__PURE__*/React.createElement(Alert, {
      bsStyle: "warning"
    }, /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("strong", null, "Form unavailable."), " The form", " ", /*#__PURE__*/React.createElement("code", null, policyActionId), " appears to currently be unavailable for this policy."), /*#__PURE__*/React.createElement(Button, {
      onClick: handleExternalClose
    }, "Close"))));
  };

  return Pac;
}(Component);
/**
 * Take Pac's props, parse them, and return the initial state for the component.
 * @param {Object} props - See `pacPropTypes`
 */


Pac.propTypes = pacPropTypes;
Pac.defaultProps = pacPropTypeDefaults;
export { Pac as default };

function getInitialStateFromProps(props) {
  // @TODO Enhancement:
  // We could probably check prop types in the production build by either
  // writing our own prop validator function, or by using facebook's prop-types
  // It _might_ be useful information for library consumers in catching bugs
  // before they go to production. But for now, we'll just assume they are
  // implementing the component correctly.
  // Parse the `policyXmlDom` we get passed in and extract all the data values
  // we'll need throughout the app. FYI: it should be safe to pass this entire
  // `policyData` object around the app without performance issues, as it
  // shouldn't update. Once we've extracted everything we need, delete the XML.
  // FYI: if there are missing values, don't bother parsing the XML.
  var policyData = extractDataFromPolicyXml(props.policyXmlDom);
  return {
    loadState: PRELOAD,
    loadError: "",
    productFormExists: false,
    // See also `pacDataPropTypes` for more information on the shape this
    // will take. It's important to the entire app.
    // Note that any values in `pacData` will not change once <Form /> mounts,
    // so we can pass this around the entire app as a big object.
    pacData: _extends({}, Object.keys(props).reduce(function (acc, key) {
      var _extends2;

      var value = props[key]; // Don't pass on the `policyXmlDom` prop, as we no longer need it.

      if (key === "policyXmlDom") {
        return acc;
      } // If it's an `api*Url` prop key, then we'll make sure it's value has
      // the trailing slash stripped from it. This normalizes our expected values.


      if (/api.*Url/.test(key)) {
        value = value.replace(/\/+$/, "");
      }

      return _extends({}, acc, (_extends2 = {}, _extends2[key] = value, _extends2));
    }, {}), {
      // Set our own `pacId` for each unique instance of <Pac />
      // This is important, as it gets passed down to every component and is used
      // to uniquely identify itself and its instance when `connect()`ing to the
      // redux store.
      pacId: policyData.PolicyID + "__" + props.policyActionId + "__" + Date.now(),
      // Policy Data
      policyData: policyData,
      // We fetch these on component mount (as needed)
      rexFormMetadata: undefined,
      supplementalPolicyTerms: undefined
    })
  };
}